<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch, type Ref } from 'vue';
  import {
    UiModalHeader,
    SelectInput,
    IconRadio,
    DatePicker,
    ColoredButton,
    IconImage,
    ThemeProvider,
  } from '@/shared/ui';
  import EditDocumentRevision from './EditDocumentRevision.vue';
  import EditDocumentValidity from './EditDocumentValidity.vue';
  import EditDocumentFormat from './EditDocumentFormat.vue';
  import { useExternalDocumentEditModal } from '../model/useDocumentEditModal';
  import type { FormSnapshot } from '../model/useDocumentEditModal.types';
  import { EXTERNAL_DOCUMENT_NAME_PREFIX } from '../model/const';
  import { isValidDate, parseCivilDate } from '@/shared/lib';
  import type { DocumentRevision } from '@/entities/Document';
  import { useUserStore } from '@/entities/User';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';

  const startOfToday = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const props = defineProps<{
    title: string;
    revision: DocumentRevision | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'saved'): void;
  }>();

  const handleClose = () => {
    emit('close');
  };

  const notificationMembers = computed(() => props.revision?.notifications?.members ?? []);
  const userStore = useUserStore();
  const collaboratorDisplayName = computed(() => userStore.name ?? 'Usuário atual');
  const initialexternalFormData = ref<FormSnapshot | null>(null);

  const initialHtmRows =
    props.revision?.physicalLocations && props.revision.physicalLocations.length > 0
      ? props.revision.physicalLocations.map((location) => ({
          local: String(location.id),
          copies: location.distribution?.quantity ?? 0,
        }))
      : [{ local: '', copies: 0 }];

  const getExternalInitialStatus = () => {
    if (props.revision?.status === 'CURRENT') return 'vigente';
    if (props.revision?.status === 'OBSOLETE') return 'revogado';
    return '';
  };

  const parseExternalConfigurationDate = (): Date => {
    const configuredAt = props.revision?.config?.configuredAt;
    if (configuredAt) {
      const parsed = parseCivilDate(configuredAt);
      if (parsed && isValidDate(parsed)) return parsed;
    }
    return startOfToday();
  };

  const fullDocumentName = props.revision?.document?.name ?? '';
  const externalFormData = reactive({
    name: fullDocumentName.startsWith(EXTERNAL_DOCUMENT_NAME_PREFIX)
      ? fullDocumentName.slice(EXTERNAL_DOCUMENT_NAME_PREFIX.length)
      : fullDocumentName,
    hasRevision: props.revision?.externalRevision ? 'true' : 'false',
    externalRevision: props.revision?.externalRevision ?? '',
    hasValidity: props.revision?.validityDate ? 'true' : 'false',
    format: props.revision?.config?.format ?? 'EL',
    status: getExternalInitialStatus(),
    includeInMasterList: String(props.revision?.config?.includeInMasterList ?? true),
    collaboratorId: String(userStore.memberId ?? ''),
    positionId: String(props.revision?.config?.editedByPosition?.id ?? ''),
    configurationDate: parseExternalConfigurationDate(),
    validityDate: parseCivilDate(props.revision?.validityDate ?? undefined),
    notificationDates: (props.revision?.notifications?.dates ?? [])
      .map((d) => parseCivilDate(d))
      .filter((d): d is Date => d !== null),
    notificationMemberIds:
      props.revision?.notifications?.members?.map((member) => member.id) ?? ([] as number[]),
    htmRows: initialHtmRows,
  });

  const notifiedUsersCount = ref(0);

  watch(
    () => userStore.memberId,
    (newMemberId) => {
      externalFormData.collaboratorId = String(newMemberId ?? '');
    },
    { immediate: true }
  );

  watch(
    () => props.revision?.id,
    (revisionId) => {
      if (revisionId == null) return;
      const today = startOfToday();
      externalFormData.configurationDate = today;
      if (initialexternalFormData.value) {
        initialexternalFormData.value.configurationDate = new Date(today);
      }
    }
  );

  const handleExternalNotifiedMemberIds = (ids: number[]) => {
    externalFormData.notificationMemberIds = ids;
  };

  const notificationUnchanged = computed(() => {
    if (!initialexternalFormData.value) return false;

    return (
      JSON.stringify(externalFormData.notificationDates) ===
        JSON.stringify(initialexternalFormData.value.notificationDates) &&
      JSON.stringify(externalFormData.notificationMemberIds) ===
        JSON.stringify(initialexternalFormData.value.notificationMemberIds)
    );
  });

  const {
    positionOptions,
    positionsResponse,
    formatDisplayDate,
    handleSave,
    canSave,
    openDatePicker,
    isDatePickerOpen,
  } = useExternalDocumentEditModal(
    props,
    emit,
    externalFormData,
    notifiedUsersCount as Ref<number>,
    notificationUnchanged
  );

  const toDateTimestamp = (value: Date | string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  };

  const hasFormChanges = computed(() => {
    if (!initialexternalFormData.value) return false;

    return (
      externalFormData.name !== initialexternalFormData.value.name ||
      externalFormData.hasRevision !== initialexternalFormData.value.hasRevision ||
      externalFormData.externalRevision !== initialexternalFormData.value.externalRevision ||
      externalFormData.hasValidity !== initialexternalFormData.value.hasValidity ||
      externalFormData.format !== initialexternalFormData.value.format ||
      externalFormData.status !== initialexternalFormData.value.status ||
      externalFormData.includeInMasterList !== initialexternalFormData.value.includeInMasterList ||
      externalFormData.collaboratorId !== initialexternalFormData.value.collaboratorId ||
      externalFormData.positionId !== initialexternalFormData.value.positionId ||
      toDateTimestamp(externalFormData.configurationDate) !==
        toDateTimestamp(initialexternalFormData.value.configurationDate) ||
      toDateTimestamp(externalFormData.validityDate) !==
        toDateTimestamp(initialexternalFormData.value.validityDate) ||
      !notificationUnchanged.value ||
      JSON.stringify(externalFormData.htmRows) !==
        JSON.stringify(initialexternalFormData.value.htmRows)
    );
  });

  onMounted(() => {
    initialexternalFormData.value = {
      name: externalFormData.name,
      hasRevision: externalFormData.hasRevision,
      externalRevision: externalFormData.externalRevision,
      hasValidity: externalFormData.hasValidity,
      format: externalFormData.format,
      status: externalFormData.status,
      includeInMasterList: externalFormData.includeInMasterList,
      collaboratorId: externalFormData.collaboratorId,
      positionId: externalFormData.positionId,
      configurationDate: externalFormData.configurationDate
        ? new Date(externalFormData.configurationDate)
        : null,
      validityDate: externalFormData.validityDate,
      notificationDates: [...externalFormData.notificationDates],
      notificationMemberIds: [...externalFormData.notificationMemberIds],
      htmRows: JSON.parse(JSON.stringify(externalFormData.htmRows)),
    };
  });
</script>

<template>
  <div class="flex flex-col bg-white-01 rounded-2xl w-[85rem] max-h-[90vh] overflow-hidden">
    <ThemeProvider class="flex flex-col min-h-0">
      <UiModalHeader
        class="shrink-0 px-6 py-4 text-sm border border-gray-02"
        :title="props.title"
        @close="handleClose"
      />
      <div
        id="modal-content"
        class="flex flex-col flex-1 min-h-0 overflow-auto"
      >
        <div class="flex flex-col divide-y divide-gray-02">
          <!-- Nome do Documento -->
          <div class="flex flex-col gap-2 p-6">
            <label
              for="name"
              class="text-sm font-semibold text-gray-07"
            >Nome do Documento:</label>
            <input
              id="name"
              v-model="externalFormData.name"
              type="text"
              class="w-full p-3 border border-gray-02 rounded-md text-gray-07 focus:outline-none focus:border-gray-07"
              placeholder="Nome do Documento Externo"
            >
          </div>

          <EditDocumentRevision
            v-model="externalFormData.externalRevision"
            v-model:has-revision="externalFormData.hasRevision"
          />

          <EditDocumentValidity
            :id="props.revision?.id"
            v-model:has-validity="externalFormData.hasValidity"
            v-model:validity-date="externalFormData.validityDate"
            v-model:notification-dates="externalFormData.notificationDates"
            :positions-response="positionsResponse"
            :notification-members="notificationMembers"
            :validate-from-today="true"
            @update:notified-users-count="notifiedUsersCount = $event"
            @update:notified-member-ids="handleExternalNotifiedMemberIds"
          />

          <EditDocumentFormat
            v-model:format="externalFormData.format"
            v-model:rows="externalFormData.htmRows"
          />

          <div class="flex flex-col gap-3 p-6">
            <label class="text-sm font-semibold text-gray-07">Status:</label>
            <div class="flex gap-6">
              <IconRadio
                v-model="externalFormData.status"
                value="vigente"
                label="Vigente"
                name="status"
              />
              <IconRadio
                v-model="externalFormData.status"
                value="revogado"
                label="Revogado"
                name="status"
              />
            </div>
          </div>

          <div class="flex flex-col gap-3 p-6">
            <label class="text-sm font-semibold text-gray-07">Incluir na Lista Mestra:</label>
            <div class="flex gap-6">
              <IconRadio
                v-model="externalFormData.includeInMasterList"
                value="true"
                label="Sim"
                name="includeInMasterList"
              />
              <IconRadio
                v-model="externalFormData.includeInMasterList"
                value="false"
                label="Não"
                name="includeInMasterList"
              />
            </div>
          </div>

          <div class="grid grid-cols-3 gap-4 p-6">
            <div class="flex text-sm flex-col gap-2">
              <label class="font-semibold text-gray-07">Colaborador:</label>
              <input
                :value="collaboratorDisplayName"
                type="text"
                disabled
                class="w-full p-3 border border-gray-02 rounded-md text-gray-07 bg-gray-50 cursor-not-allowed"
              >
            </div>

            <div class="flex text-sm flex-col gap-2">
              <label class="font-semibold text-gray-07">Cargo:</label>
              <SelectInput
                v-model="externalFormData.positionId"
                :options="positionOptions"
                label="Selecione o cargo"
              />
            </div>

            <div class="flex flex-col gap-2">
              <label class="text-sm font-semibold text-gray-07">Data da configuração:</label>
              <button
                type="button"
                class="flex items-center gap-2 w-2/3 p-3 border border-gray-02 rounded-lg text-gray-07 focus:outline-none focus:border-gray-07 bg-white-01"
                @click="openDatePicker"
              >
                <IconImage
                  :src="CalendarIcon"
                  alt="Calendar"
                />
                <span class="text-left text-sm">{{
                  formatDisplayDate(externalFormData.configurationDate)
                }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="shrink-0 flex justify-end gap-3 p-6 border-t border-gray-02">
        <ColoredButton
          text="Cancelar"
          outlined
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :icon="CheckMarkIcon"
          :disabled="!hasFormChanges || !canSave"
          @click="handleSave"
        />
      </div>

      <DatePicker
        v-model:is-open="isDatePickerOpen"
        :selected-date="externalFormData.configurationDate ?? null"
        :disable-future-dates="true"
        @update:selected-date="
          (date: Date | null) => (externalFormData.configurationDate = date ?? startOfToday())
        "
      />
    </ThemeProvider>
  </div>
</template>
